import React from 'react';
import ReactDOM from 'react-dom/client';
import { unstableSetRender } from 'antd';
import './index.css';
import './styles/statusAnimations.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { logger } from './utils/logger';
import { setupAxiosAuth } from './services/setupAxiosAuth';

// Attach the JWT to bare `axios.*` calls (services/api/* modules) so
// authenticate-protected routes (fees, DVI, attachments, audit trail, time
// entries, knowledge-base) stop returning 401. Must run before any request.
setupAxiosAuth();

// React 19 compatibility for Ant Design v5.
//
// antd v5's imperative/static APIs (message.success/error, notification.*,
// Modal.confirm) render through ReactDOM.render, which React 19 removed. The
// result is that every static message.* call silently renders nothing — so
// actions like saving a line item give no feedback and appear to "do nothing".
// Routing antd's imperative rendering through React 19's createRoot restores
// all ~585 static call sites at once. Equivalent to the official
// @ant-design/v5-patch-for-react-19, inlined to avoid an extra dependency.
// Must run before any antd static method is invoked.
unstableSetRender((node, container) => {
  const c = container as Element & { _reactRoot?: ReturnType<typeof ReactDOM.createRoot> };
  c._reactRoot ||= ReactDOM.createRoot(c);
  const root = c._reactRoot;
  root.render(node);
  return async () => {
    await new Promise((resolve) => setTimeout(resolve, 0));
    root.unmount();
  };
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

// Only use StrictMode in development to avoid double-rendering in production
const app = process.env.NODE_ENV === 'development'
  ? <React.StrictMode><App /></React.StrictMode>
  : <App />;

root.render(app);

// Initialize PWA functionality only in production
if (process.env.NODE_ENV === 'production') {
  // Conditionally import PWA manager only in production
  import('./utils/pwa').then((module) => {
    const PWAManager = module.default;
    const pwaManager = new PWAManager();
    logger.log('[PWA] Initializing PWA features...');

    // Listen for service worker messages
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'CACHE_UPDATED') {
          logger.log('[PWA] Cache updated, showing refresh prompt');

          // Show update notification
          const updateBanner = document.createElement('div');
          updateBanner.innerHTML = `
            <div class="fixed top-4 left-4 right-4 bg-blue-600 text-white rounded-lg p-4 z-50 md:left-auto md:right-4 md:max-w-sm" style="position: fixed; top: 16px; right: 16px; background: #2563eb; color: white; border-radius: 8px; padding: 16px; z-index: 9999; max-width: 320px; box-shadow: 0 10px 25px rgba(0,0,0,0.2);">
              <div style="display: flex; align-items: center; justify-content: space-between;">
                <div>
                  <h3 style="font-weight: 600; font-size: 14px; margin: 0 0 4px 0;">App Updated!</h3>
                  <p style="font-size: 12px; margin: 0; opacity: 0.9;">A new version is available. Refresh to get the latest features.</p>
                </div>
                <button id="refresh-btn" style="background: white; color: #2563eb; border: none; padding: 8px 12px; border-radius: 4px; font-size: 12px; font-weight: 600; cursor: pointer; margin-left: 12px;">
                  Refresh
                </button>
              </div>
            </div>
          `;

          document.body.appendChild(updateBanner);

          updateBanner.querySelector('#refresh-btn')?.addEventListener('click', () => {
            window.location.reload();
          });

          // Auto dismiss after 10 seconds
          setTimeout(() => {
            if (document.body.contains(updateBanner)) {
              document.body.removeChild(updateBanner);
            }
          }, 10000);
        }
      });
    }
  }).catch((error) => {
    logger.error('[PWA] Failed to initialize PWA manager:', error);
  });
} else {
  // Unregister any existing service workers in development mode
  logger.log('[PWA] PWA features disabled in development mode');

  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      if (registrations.length > 0) {
        logger.log(`[PWA] Found ${registrations.length} registered service worker(s), unregistering...`);
        registrations.forEach((registration) => {
          registration.unregister().then((success) => {
            if (success) {
              logger.log('[PWA] Service worker unregistered successfully');
            }
          });
        });
      } else {
        logger.log('[PWA] No service workers registered');
      }
    }).catch((error) => {
      logger.error('[PWA] Failed to check service worker registrations:', error);
    });
  }
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
